// BFF-based authentication.
//
// Tokens are stored exclusively as HttpOnly cookies by the BFF server.
// JavaScript never reads or writes tokens — the BFF injects the
// Authorization: Bearer header server-side for every /api/* request.

export type { AuthResult } from './backend-types/authentication';
import type { AuthResult } from './backend-types/authentication';

/**
 * Checks authentication state by calling the BFF /auth/me endpoint.
 * The BFF reads the HttpOnly access-token cookie and returns user identity.
 * Returns { authenticated: false } when no valid session exists.
 */
export async function initAuth(): Promise<AuthResult> {
  const res = await fetch('/auth/me', { credentials: 'same-origin' });
  if (!res.ok) return { authenticated: false };
  const {
    name,
    given_name,
    family_name,
    preferred_username,
    email,
    hco,
    locale,
    userRole,
    organization_name,
  } = (await res.json()) as {
    name?: string;
    given_name?: string;
    family_name?: string;
    preferred_username?: string;
    email?: string;
    hco?: string;
    locale?: string;
    userRole?: string;
    organization_name?: string;
  };
  return {
    authenticated: true,
    name,
    given_name,
    family_name,
    preferred_username,
    email,
    hco,
    locale,
    userRole,
    organization_name,
  };
}

/**
 * Redirect the browser to the BFF login endpoint.
 * The BFF generates the PKCE challenge and redirects to Keycloak.
 * After authentication Keycloak redirects to /auth/callback (BFF),
 * which exchanges the code, sets HttpOnly cookies, and redirects to /.
 */
export function login(options?: { realm?: string; loginHint?: string; prompt?: 'login' }): void {
  const url = new URL('/auth/login', window.location.origin);
  if (options?.realm) url.searchParams.set('realm', options.realm);
  if (options?.loginHint) url.searchParams.set('login_hint', options.loginHint);
  if (options?.prompt === 'login') url.searchParams.set('prompt', 'login');
  window.location.href = url.toString();
}

/**
 * Redirect the browser to the BFF logout endpoint.
 * The BFF revokes the Keycloak session, clears HttpOnly cookies,
 * and redirects to /.
 */
export function logout(): void {
  window.location.href = '/auth/logout';
}

export async function localLogout(): Promise<void> {
  await fetch('/auth/local-logout', { method: 'POST', credentials: 'same-origin' });
}

/**
 * Asks the BFF to rotate the tokens using the HttpOnly refresh-token cookie.
 * Returns true if the refresh succeeded; false if the session has expired.
 * The `minValidity` parameter is accepted for API compatibility but expiry
 * is enforced server-side by the BFF.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export async function refreshToken(minValidity = 30): Promise<boolean> {
  const res = await fetch('/auth/refresh', { method: 'POST', credentials: 'same-origin' });
  return res.ok;
}
